import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Pencil } from 'lucide-react';

export function EditChallanDialog({ open, onOpenChange, editChallan, editForm, setEditForm, handleEditItemChange, submitChallanEdit, editLoading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="edit-challan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil size={18} className="text-blue-600" />
            Edit Challan {editChallan?.challan_number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Transport Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Vehicle No.</Label>
              <Input
                value={editForm.vehicle_no}
                onChange={(e) => setEditForm(prev => ({ ...prev, vehicle_no: e.target.value }))}
                className="mt-1 h-8 rounded-sm text-sm"
                data-testid="edit-vehicle-no"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Transporter</Label>
              <Input
                value={editForm.transporter}
                onChange={(e) => setEditForm(prev => ({ ...prev, transporter: e.target.value }))}
                className="mt-1 h-8 rounded-sm text-sm"
                data-testid="edit-transporter"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Invoice No.</Label>
              <Input
                value={editForm.invoice_no}
                onChange={(e) => setEditForm(prev => ({ ...prev, invoice_no: e.target.value }))}
                className="mt-1 h-8 rounded-sm text-sm"
                data-testid="edit-invoice-no"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Total Cartons</Label>
              <Input
                type="number"
                value={editForm.total_cartons}
                onChange={(e) => setEditForm(prev => ({ ...prev, total_cartons: e.target.value }))}
                className="mt-1 h-8 rounded-sm text-sm"
                data-testid="edit-total-cartons"
              />
            </div>
          </div>

          {/* Items Table */}
          {editForm.items.length > 0 && (
            <div className="border border-zinc-200 rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">PO No.</th>
                    <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">SKU</th>
                    <th className="text-center p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-14">Size</th>
                    <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-24">Brand</th>
                    <th className="text-center p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-20">Qty</th>
                    <th className="text-center p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-20">Rate</th>
                    <th className="text-center p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-24">Job Card</th>
                  </tr>
                </thead>
                <tbody>
                  {editForm.items.map((item, idx) => (
                    <tr key={`${item.sku}-${item.size}`} className="border-t border-zinc-200">
                      <td className="p-2 font-mono text-xs text-zinc-600">{item.po_number || '-'}</td>
                      <td className="p-2 font-mono text-xs">{item.sku}</td>
                      <td className="p-2 text-center font-mono text-xs">{item.size}</td>
                      <td className="p-2">
                        <Input
                          value={item.brand}
                          onChange={(e) => handleEditItemChange(idx, 'brand', e.target.value)}
                          className="h-7 rounded-sm text-xs"
                          data-testid={`edit-item-brand-${idx}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min="0"
                          value={item.dispatch_qty}
                          onChange={(e) => handleEditItemChange(idx, 'dispatch_qty', e.target.value)}
                          className="h-7 rounded-sm font-mono text-xs text-center"
                          data-testid={`edit-item-qty-${idx}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.rate}
                          onChange={(e) => handleEditItemChange(idx, 'rate', e.target.value)}
                          className="h-7 rounded-sm font-mono text-xs text-center"
                          data-testid={`edit-item-rate-${idx}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.job_card_number}
                          onChange={(e) => handleEditItemChange(idx, 'job_card_number', e.target.value)}
                          className="h-7 rounded-sm font-mono text-xs"
                          data-testid={`edit-item-jc-${idx}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Audit info */}
          {editChallan?.last_edited_by && (
            <p className="text-xs text-zinc-400">
              Last edited by {editChallan.last_edited_by} on {editChallan.last_edited_at}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-zinc-200">
            <Button variant="outline" className="h-9 rounded-sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              className="h-9 rounded-sm bg-blue-600 hover:bg-blue-700 text-white"
              onClick={submitChallanEdit}
              disabled={editLoading}
              data-testid="submit-edit-challan"
            >
              {editLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
